<template>
  <!-- 登录弹窗：不在页面里放独立登录页跳转，改成"该登录时才弹" -->
  <el-dialog
    :visible="visible"
    width="400px"
    :close-on-click-modal="false"
    :show-close="true"
    append-to-body
    custom-class="login-dialog"
    @close="onClose"
  >
    <div class="ld-head">
      <div class="ld-badge"><img class="ld-logo" :src="logoImg" alt="創視魔方" /></div>
      <div class="ld-title">{{ isRegister ? '註冊創視魔方' : '登入創視魔方' }}</div>
      <div class="ld-sub">
        {{ isRegister
          ? '填寫邀請碼即可註冊，註冊後自動登入'
          : '登入後，用 AI 產生電商主圖、詳情圖與短影片' }}
      </div>
    </div>

    <!-- 登入 / 註冊 切換：做成同一弹窗里的两个页签，不用跳页面 -->
    <div class="ld-tabs">
      <span class="ld-tab" :class="{ on: !isRegister }" @click="switchMode(false)">登入</span>
      <span class="ld-tab" :class="{ on: isRegister }" @click="switchMode(true)">註冊</span>
    </div>

    <el-form label-position="top" @submit.native.prevent>
      <el-form-item v-if="!isRegister">
        <el-input
          v-model="form.username"
          prefix-icon="el-icon-user"
          placeholder="使用者名稱 / 帳號"
          size="large"
        />
      </el-form-item>
      <el-form-item v-else>
        <el-input
          v-model="form.email"
          prefix-icon="el-icon-message"
          placeholder="信箱（之後用它登入）"
          size="large"
        />
      </el-form-item>

      <el-form-item>
        <el-input
          v-model="form.password"
          type="password"
          show-password
          prefix-icon="el-icon-lock"
          :placeholder="isRegister ? '密碼（至少 6 位）' : '密碼'"
          size="large"
          @keyup.enter.native="isRegister ? doRegister() : doLogin()"
        />
      </el-form-item>

      <!-- 註冊專屬欄位 -->
      <template v-if="isRegister">
        <el-form-item>
          <!-- 邀請碼是邀請制的入口，必填 -->
          <el-input
            v-model="form.inviteCode"
            prefix-icon="el-icon-tickets"
            placeholder="邀請碼（必填）"
            size="large"
            @keyup.enter.native="doRegister"
          />
        </el-form-item>
        <el-form-item>
          <el-input
            v-model="form.nickname"
            prefix-icon="el-icon-user-solid"
            placeholder="暱稱（選填）"
            size="large"
          />
        </el-form-item>
      </template>

      <el-form-item>
        <div class="ld-captcha">
          <el-input
            v-model="form.captcha"
            prefix-icon="el-icon-key"
            placeholder="驗證碼"
            size="large"
            @keyup.enter.native="isRegister ? doRegister() : doLogin()"
          />
          <img
            :src="captchaSrc"
            class="ld-captcha-img"
            title="點擊重新整理驗證碼"
            @click="loadCaptcha"
          />
        </div>
      </el-form-item>
    </el-form>

    <el-button
      type="primary"
      class="ld-btn"
      :loading="loading"
      @click="isRegister ? doRegister() : doLogin()"
    >
      {{ loading
        ? (isRegister ? '註冊中…' : '驗證中…')
        : (isRegister ? '註冊' : '登入') }}
    </el-button>

    <!-- 底下再给一条明确的切换入口：
         只有页签的话，第一次来的人容易只看到「登入」就以为不能注册。
         这里把两个动词都摆在台面上，且都能直接点。 -->
    <div class="ld-switch">
      <template v-if="isRegister">
        已經有帳號了？<a class="ld-link" @click="switchMode(false)">登入</a>
      </template>
      <template v-else>
        還沒有帳號？<a class="ld-link" @click="switchMode(true)">註冊</a>
      </template>
    </div>

    <div class="ld-foot">創視魔方 · AI 電商業務引擎</div>
  </el-dialog>
</template>

<script>
import logoImg from '../assets/img/logo.png'
// 注册表单校验：与独立登录页（views/Login.vue）共用同一份规则
import { validateRegisterForm } from '../utils/validate'
const captchaUrl = () => '/api/v1/captcha?rnd=' + Date.now()

export default {
  name: 'LoginDialog',
  data() {
    return {
      logoImg,
      loading: false,
      // false=登入 / true=註冊。兩個模式共用一個彈窗，切換時不關彈窗
      isRegister: false,
      captchaSrc: captchaUrl(),
      form: {
        username: '',
        password: '',
        captcha: '',
        email: '',
        inviteCode: '',
        nickname: ''
      }
    }
  },
  computed: {
    visible() {
      return this.$store.state.loginVisible
    },
    redirect() {
      return this.$store.state.loginRedirect
    }
  },
  methods: {
    /** 刷新验证码（点击图片/登录失败后调用），并清空已输入验证码 */
    loadCaptcha() {
      this.captchaSrc = captchaUrl()
      this.form.captcha = ''
    },
    /** 切換登入 / 註冊。驗證碼是一次性的，切換時要换一张新的 */
    switchMode(reg) {
      if (this.loading) {
        return
      }
      this.isRegister = reg
      this.loadCaptcha()
    },
    onClose() {
      this.$store.dispatch('closeLogin')
      this.form.password = ''
      this.loadCaptcha()
    },
    async doLogin() {
      if (!this.form.username || !this.form.password) {
        this.$message.warning('請填寫使用者名稱和密碼')
        return
      }
      if (!this.form.captcha) {
        this.$message.warning('請輸入驗證碼')
        return
      }
      this.loading = true
      try {
        await this.$store.dispatch('doLogin', {
          username: this.form.username,
          password: this.form.password,
          captcha: this.form.captcha
        })
        const to = this.redirect
        this.$store.dispatch('closeLogin')
        this.$message.success('登入成功')
        this.form.password = ''
        this.loadCaptcha()
        if (to) {
          this.$router.push(to).catch(() => {})
        }
      } catch (e) {
        this.$message.error(e.message || '登入失敗')
        this.loadCaptcha() // 刷新验证码 + 清空输入
      } finally {
        this.loading = false
      }
    },
    /**
     * 註冊（邀請制）：信箱 + 密碼 + 邀請碼。
     *
     * 校验规则走 utils/validate.js，和独立登录页（views/Login.vue）共用同一份 ——
     * 两个入口必须判得一样，否则会出现「这边能注册、那边不行」。
     *
     * 前端拦一道是为了少一次「填完整张表才被后端退回来」；而验证码是一次性的，
     * 一来一回还要多刷一次。
     */
    async doRegister() {
      const f = this.form
      const err = validateRegisterForm(f)
      if (err) {
        this.$message.warning(err)
        return
      }
      this.loading = true
      try {
        await this.$store.dispatch('doRegister', {
          email: f.email.trim(),
          password: f.password,
          nickname: (f.nickname || '').trim(),
          invite_code: f.inviteCode.trim(),
          captcha: f.captcha
        })
        const to = this.redirect
        this.$store.dispatch('closeLogin')
        // 後端註冊成功就回了 token，所以這裡直接就是登入狀態
        this.$message.success('註冊成功，已自動登入')
        this.form.password = ''
        this.form.inviteCode = ''
        this.loadCaptcha()
        if (to) {
          this.$router.push(to).catch(() => {})
        }
      } catch (e) {
        this.$message.error(e.message || '註冊失敗')
        this.loadCaptcha()
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<!-- 非 scoped：el-dialog 用了 append-to-body，scoped 属性选择器匹配不到，统一用 .login-dialog 前缀隔离 -->
<style>
/* 非 scoped：el-dialog 用 append-to-body，scoped 属性选择器匹配不到，统一用 .login-dialog 前缀隔离 */
.login-dialog {
  border-radius: 16px;
  overflow: hidden;
}
/* header 留空：关闭按钮绝对定位到右上角，不占高度 */
.login-dialog .el-dialog__header {
  padding: 0;
}
.login-dialog .el-dialog__headerbtn {
  top: 14px;
  right: 14px;
  z-index: 3;
  font-size: 17px;
}
.login-dialog .el-dialog__body {
  padding: 36px 32px 24px;
}

/* —— 头部：图标 + 标题 + 副标题 —— */
.login-dialog .ld-head {
  text-align: center;
  margin-bottom: 22px;
}
.login-dialog .ld-badge {
  width: 48px;
  height: 48px;
  margin: 0 auto 12px;
  border-radius: 14px;
  overflow: hidden;
  /* 图自带白底，容器同色才不会露出色块 */
  background: #fff;
}
.login-dialog .ld-logo {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.login-dialog .ld-title {
  font-size: 18px;
  font-weight: 600;
  color: var(--c-text-1);
  letter-spacing: 0.3px;
}
.login-dialog .ld-sub {
  margin-top: 7px;
  font-size: 12px;
  line-height: 1.5;
  color: var(--c-text-3);
}

/* —— 登入 / 註冊 切換頁籤：分段控件樣式，選中的那半浮起來 —— */
.login-dialog .ld-tabs {
  display: flex;
  gap: 4px;
  margin: -6px 0 18px;
  padding: 3px;
  border-radius: 10px;
  background: var(--c-surface-3);
}
.login-dialog .ld-tab {
  flex: 1;
  padding: 8px 0;
  text-align: center;
  border-radius: 8px;
  font-size: 14px;
  color: var(--c-text-3);
  cursor: pointer;
  user-select: none;
  transition: background-color 0.16s ease, color 0.16s ease;
}
.login-dialog .ld-tab:hover {
  color: var(--c-text-2);
}
.login-dialog .ld-tab.on {
  color: var(--c-text-1);
  font-weight: 600;
  background: var(--c-surface);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

/* —— 输入框：浅底 + 细边，聚焦时转白底 + 主色光环 —— */
.login-dialog .el-form-item {
  margin-bottom: 14px;
}
.login-dialog .el-input__inner {
  height: 42px;
  border-radius: 10px;
  border-color: var(--c-border-2);
  background: var(--c-surface-2);
  color: var(--c-text-1);
  padding-left: 40px;
  transition: border-color 0.16s ease, background-color 0.16s ease, box-shadow 0.16s ease;
}
.login-dialog .el-input__inner:hover {
  border-color: var(--c-primary-line);
  background: var(--c-surface);
}
.login-dialog .el-input__inner:focus {
  background: var(--c-surface);
  border-color: var(--c-primary);
  box-shadow: 0 0 0 3px rgba(30, 64, 175, 0.1);
}
.login-dialog .el-input__icon {
  color: var(--c-text-3);
  line-height: 42px;
}
.login-dialog .el-input__inner::placeholder {
  color: var(--c-text-4);
}

/* —— 验证码：图片固定尺寸，不被输入框挤压 —— */
.login-dialog .ld-captcha {
  display: flex;
  gap: 10px;
  align-items: center;
}
.login-dialog .ld-captcha .el-input {
  flex: 1;
  min-width: 0;
}
.login-dialog .ld-captcha-img {
  flex: none;
  width: 108px;
  height: 42px;
  border-radius: 10px;
  border: 1px solid var(--c-border-2);
  background: var(--c-surface-3);
  cursor: pointer;
  object-fit: cover;
  transition: border-color 0.16s ease;
}
.login-dialog .ld-captcha-img:hover {
  border-color: var(--c-primary-line);
}

/* —— 登录按钮 —— */
.login-dialog .ld-btn {
  width: 100%;
  height: 44px;
  margin-top: 4px;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 2px;
  border: none;
  border-radius: 10px;
  color: #fff;
  background: var(--c-primary);
  box-shadow: 0 4px 14px rgba(30, 64, 175, 0.22);
  transition: background-color 0.16s ease, box-shadow 0.16s ease;
}
.login-dialog .ld-btn:hover {
  background: var(--c-primary-hover);
  box-shadow: 0 6px 18px rgba(30, 64, 175, 0.3);
}

/* —— 主按钮下方：登入 / 註冊 的互相切換入口 —— */
.login-dialog .ld-switch {
  margin-top: 14px;
  text-align: center;
  font-size: 13px;
  color: var(--c-text-3);
}
.login-dialog .ld-link {
  color: var(--c-primary);
  font-weight: 600;
  cursor: pointer;
  margin-left: 2px;
}
.login-dialog .ld-link:hover {
  text-decoration: underline;
}

/* —— 底部说明 —— */
.login-dialog .ld-foot {
  margin-top: 18px;
  text-align: center;
  font-size: 12px;
  color: var(--c-text-4);
}
</style>
